import { useCallback } from 'react';
import { useEditor, activeMasterOf } from '../context/EditorContext';
import { isFreeform, toFreeform } from '../lib/freeform';
import { toast } from 'sonner';

const activeObjectsOf = (s) => (s.viewMode === 'master' ? activeMasterOf(s)?.objects || [] : [...(s.pages[s.currentPageIndex]?.objects || []), ...(s.scratch?.objects || [])]);

/** Shapes (any preset or freeform, not lines inside groups) can enter Edit Points. */
export const canEditPoints = (o) => !!o && o.type === 'shape';

/**
 * Publisher "Edit Shape ▸ Edit Points": converts the selected preset shape into a freeform (once) and enters the
 * vertex-editing mode (`state.pointEditId`). The reducer clears the mode when the object stops being the single,
 * unlocked selection or is deleted.
 */
export const useEditPoints = () => {
  const { state, stateRef, dispatch, rawDispatch } = useEditor();
  const find = useCallback((id) => activeObjectsOf(stateRef.current).find((o) => o.id === id), [stateRef]);

  const enter = useCallback((objectId) => {
    const s = stateRef.current;
    const id = objectId || (s.selectedObjectIds.length === 1 ? s.selectedObjectIds[0] : null);
    const o = id ? find(id) : null;
    if (!o) { toast.info('Select a shape first.'); return false; }
    if (!canEditPoints(o)) { toast.info('Edit Points works on shapes and lines.'); return false; }
    if (o.locked) { toast.info('Unlock the shape to edit its points.'); return false; }
    if (!isFreeform(o)) {
      const patch = toFreeform(o);
      if (!patch) return false;
      dispatch({ type: 'UPDATE_OBJECT', payload: { id: o.id, data: patch } });
    }
    if (s.activeTool !== 'select') rawDispatch({ type: 'SET_ACTIVE_TOOL', payload: 'select' });
    if (s.editingObjectId) rawDispatch({ type: 'SET_EDITING', payload: null });
    dispatch({ type: 'SELECT_OBJECT', payload: { id: o.id } });
    rawDispatch({ type: 'SET_POINT_EDIT', payload: o.id });
    try {
      if (!sessionStorage.getItem('wp-edit-points-hint')) {
        sessionStorage.setItem('wp-edit-points-hint', '1');
        toast.info('Drag a point or a segment to move it. Ctrl+click a segment to add a point, Ctrl+click a point to delete it. Right-click a point or segment for more. Esc or Done to finish.', { id: 'edit-points-hint', duration: 7000 });
      }
    } catch { /* storage unavailable */ }
    return true;
  }, [stateRef, find, dispatch, rawDispatch]);

  const exit = useCallback(() => { if (stateRef.current.pointEditId) rawDispatch({ type: 'SET_POINT_EDIT', payload: null }); }, [stateRef, rawDispatch]);

  const editing = state.pointEditId ? activeObjectsOf(state).find((o) => o.id === state.pointEditId) || null : null;
  return { enter, exit, editing, canEdit: canEditPoints, isFreeform };
};
